import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  useMediaQuery,
  useTheme,
  Grid,
  LinearProgress,
  Tabs,
  Tab,
  Stack,
  Divider,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Snackbar,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Backup as BackupIcon,
  Restore as RestoreIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  CloudDownload as CloudDownloadIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Preview as PreviewIcon,
  Storage as StorageIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  DeviceHub as DeviceHubIcon,
  ListAlt as ListAltIcon,
  AttachMoney as PriceIcon
} from '@mui/icons-material';

// 🔥 가격 백업 탭 컴포넌트
import { PriceBackupTab } from './components/priceBackup';
import { useSearchParams } from 'react-router-dom';

/**
 * 중고 디지털 기기 매입 프로세스 백업/복원 관리 페이지
 * [배경] applications, alerts_log, admins 컬렉션의 안전한 백업/복원으로
 * 매입 신청, 본인인증, 알림톡 발송 이력 등 핵심 비즈니스 데이터 보호
 */

// Firebase Functions API 베이스 URL
const API_BASE_URL = 'https://api-4iuywcitfq-uc.a.run.app';

interface BackupItem {
  id: string;
  backup_id: string;
  status: 'completed' | 'failed' | 'in_progress';
  type: 'scheduled' | 'manual';
  created_at: string;
  total_documents: number;
  applications_count: number;
  alerts_count: number;
  admins_count: number;
  file_size_mb: string;
  file_exists: boolean;
  is_expired: boolean;
  days_until_expiry: number | null;
  gcs_path?: string;
  error_message?: string;
  domain_info?: {
    is_purchase_data: boolean;
    contains_applications: boolean;
    contains_alerts: boolean;
    contains_admins: boolean;
    estimated_customers: number;
  };
}

interface RestorePreview {
  success?: boolean;
  backup_id: string;
  backup_info: {
    created_at: string;
    backup_version: string;
    domain: string;
    total_documents: number;
  };
  comparison_results: {
    [collection: string]: {
      backup_count: number;
      current_count: number;
      impact_analysis: {
        will_be_added: number;
        will_be_lost: number;
        will_be_modified: number;
        data_loss_risk: 'low' | 'medium' | 'high';
      };
      process_statistics?: any;
    };
  };
  overall_impact: {
    risk_level: 'low' | 'medium' | 'high';
    total_data_loss: number;
    total_modifications: number;
    recommendation: string;
  };
  recommendations: Array<{
    type: 'critical' | 'warning' | 'safe' | 'collection_warning';
    message: string;
    collection?: string;
  }>;
}

interface LogItem {
  id: string;
  operation_type: 'backup' | 'restore' | 'delete';
  status: 'success' | 'failed' | 'in_progress';
  started_at: string;
  completed_at?: string;
  duration_seconds?: number;
  backup_id?: string;
  details: {
    trigger: 'manual' | 'scheduled' | 'api';
    user_id?: string;
    collections_processed?: string[];
    documents_count?: number;
    file_size_mb?: number;
    error_message?: string;
    warning_messages?: string[];
    performance_metrics?: {
      processing_time_ms: number;
      memory_usage_mb: number;
      network_transfer_mb: number;
    };
  };
  metadata: {
    project_id: string;
    function_version: string;
    region: string;
    request_id: string;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`backup-tabpanel-${index}`}
      aria-labelledby={`backup-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 2, md: 3 } }}>{children}</Box>}
    </div>
  );
}

const BackupManagementPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams] = useSearchParams();
  
  // 상태 관리 (매입 데이터 모드에서 사용하는 탭 인덱스: 0~3)
  const [activeTab, setActiveTab] = useState(0);
  const [backups, setBackups] = useState<BackupItem[]>([]);
  const [logs, setLogs] = useState<LogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  
  // 로그 삭제 관련 상태
  const [selectedLogIds, setSelectedLogIds] = useState<string[]>([]);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // 다이얼로그 상태
  const [restoreDialog, setRestoreDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState<BackupItem | null>(null);
  const [restorePreview, setRestorePreview] = useState<RestorePreview | null>(null);
  const [restoreMode, setRestoreMode] = useState<'replace' | 'merge'>('replace');
  const [collectionsToRestore, setCollectionsToRestore] = useState(['applications']);
  
  // 알림 상태
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // 백업 목록 조회
  const fetchBackups = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/backup/list`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '백업 목록 조회 실패');
      }
      
      const data = await response.json();
      setBackups(data.backups || []);
      
      setSnackbar({
        open: true,
        message: `백업 목록을 성공적으로 조회했습니다. (총 ${data.backups?.length || 0}개)`,
        severity: 'success'
      });
      
    } catch (error) {
      console.error('백업 목록 조회 실패:', error);
      setSnackbar({
        open: true,
        message: `백업 목록 조회 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // 로그 목록 조회
  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/logs/operations`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setLogs(data.logs || []);
    } catch (error: any) {
      console.error('로그 조회 실패:', error);
      setSnackbar({
        open: true,
        message: '로그를 조회할 수 없습니다.',
        severity: 'error'
      });
    } finally {
      setLogsLoading(false);
    }
  }, []);

  // 로그 CSV 내보내기
  const exportLogs = () => {
    if (logs.length === 0) {
      setSnackbar({
        open: true,
        message: '내보낼 로그가 없습니다.',
        severity: 'warning'
      });
      return;
    }

    // CSV 헤더 정의
    const headers = [
      '작업 ID',
      '작업 유형',
      '상태',
      '시작 시간',
      '완료 시간',
      '소요 시간(초)',
      '백업 ID',
      '트리거',
      '처리 문서 수',
      '파일 크기(MB)',
      '에러 메시지',
      '성능 - 처리시간(ms)',
      '성능 - 메모리(MB)',
      '프로젝트 ID',
      '리전'
    ];

    // CSV 데이터 변환
    const csvData = logs.map(log => [
      log.id,
      log.operation_type === 'backup' ? '백업' : 
      log.operation_type === 'restore' ? '복원' : '삭제',
      log.status === 'success' ? '성공' : 
      log.status === 'failed' ? '실패' : '진행중',
      log.started_at,
      log.completed_at || '',
      log.duration_seconds || '',
      log.backup_id || '',
      log.details.trigger === 'manual' ? '수동' : 
      log.details.trigger === 'scheduled' ? '자동' : 'API',
      log.details.documents_count || '',
      log.details.file_size_mb || '',
      log.details.error_message || '',
      log.details.performance_metrics?.processing_time_ms || '',
      log.details.performance_metrics?.memory_usage_mb || '',
      log.metadata.project_id,
      log.metadata.region
    ]);

    // CSV 문자열 생성
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => 
        typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
      ).join(','))
    ].join('\n');

    // 파일 다운로드
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `backup-logs-${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setSnackbar({
      open: true,
      message: `로그 ${logs.length}개를 CSV 파일로 내보냈습니다.`,
      severity: 'success'
    });
  };

  // 개별 로그 삭제
  const deleteSelectedLogs = async () => {
    if (selectedLogIds.length === 0) {
      setSnackbar({
        open: true,
        message: '삭제할 로그를 선택해주세요.',
        severity: 'warning'
      });
      return;
    }

    setDeleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/logs/operations`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          log_ids: selectedLogIds,
          confirm_delete: true
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSnackbar({
          open: true,
          message: `로그 ${data.deleted_count}개가 성공적으로 삭제되었습니다.`,
          severity: 'success'
        });
        
        setSelectedLogIds([]);
        setDeleteDialog(false);
        fetchLogs(); // 로그 목록 새로고침
      } else {
        throw new Error(data.message || '로그 삭제 실패');
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `로그 삭제 실패: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setDeleting(false);
    }
  };

  // 조건별 일괄 삭제
  const deleteBulkLogs = async (criteria: {
    older_than_days?: number;
    status?: string;
    operation_type?: string;
  }) => {
    setDeleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/logs/operations`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          delete_criteria: criteria,
          confirm_delete: true
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSnackbar({
          open: true,
          message: `조건에 맞는 로그 ${data.deleted_count}개가 삭제되었습니다.`,
          severity: 'success'
        });
        
        setBulkDeleteDialog(false);
        fetchLogs(); // 로그 목록 새로고침
      } else {
        throw new Error(data.message || '일괄 삭제 실패');
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `일괄 삭제 실패: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setDeleting(false);
    }
  };

  // 로그 선택 토글
  const toggleLogSelection = (logId: string) => {
    setSelectedLogIds(prev => 
      prev.includes(logId) 
        ? prev.filter(id => id !== logId)
        : [...prev, logId]
    );
  };

  // 전체 선택/해제
  const toggleAllLogsSelection = () => {
    if (selectedLogIds.length === logs.length) {
      setSelectedLogIds([]);
    } else {
      setSelectedLogIds(logs.map(log => log.id));
    }
  };

  // 수동 백업 생성
  const createManualBackup = async () => {
    setCreating(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/backup/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'manual' })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '백업 생성 실패');
      }
      
      const data = await response.json();
      console.log('✅ 수동 백업 생성 완료:', data);
      
      setSnackbar({
        open: true,
        message: `백업이 성공적으로 생성되었습니다! (${data.statistics?.total_documents || 0}개 문서, ${data.statistics?.file_size_mb || 0}MB)`,
        severity: 'success'
      });
      
      // 목록 새로고침
      await fetchBackups();
      
    } catch (error) {
      console.error('수동 백업 생성 실패:', error);
      setSnackbar({
        open: true,
        message: `백업 생성 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`,
        severity: 'error'
      });
    } finally {
      setCreating(false);
    }
  };

  // 복원 미리보기
  const showRestorePreview = async (backup: BackupItem) => {
    setPreviewing(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/restore/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          backup_id: backup.backup_id,
          collections: collectionsToRestore
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '미리보기 생성 실패');
      }
      
      const data = await response.json();
      setRestorePreview(data);
      setPreviewDialog(true);
      
    } catch (error) {
      console.error('복원 미리보기 실패:', error);
      setSnackbar({
        open: true,
        message: `미리보기 생성 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`,
        severity: 'error'
      });
    } finally {
      setPreviewing(false);
    }
  };

  // 백업 다운로드
  const downloadBackup = async (backup: BackupItem) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/backup/${backup.backup_id}/download`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error('다운로드 실패');
      }

      // 파일을 Blob으로 변환
      const blob = await response.blob();
      
      // 다운로드 링크 생성
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup-${backup.backup_id}.json`;
      document.body.appendChild(a);
      a.click();
      
      // 정리
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSnackbar({
        open: true,
        message: '백업 파일이 다운로드되었습니다.',
        severity: 'success'
      });

    } catch (error) {
      console.error('백업 다운로드 실패:', error);
      setSnackbar({
        open: true,
        message: '백업 다운로드에 실패했습니다.',
        severity: 'error'
      });
    }
  };

  // 백업 삭제
  const handleDeleteBackup = async (backup: BackupItem) => {
    if (!window.confirm(`'${backup.backup_id}' 백업을 정말 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/backup/${backup.backup_id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '백업 삭제 실패');
      }

      // 백업 목록 새로고침
      await fetchBackups();

      setSnackbar({
        open: true,
        message: '백업이 성공적으로 삭제되었습니다.',
        severity: 'success'
      });

    } catch (error) {
      console.error('백업 삭제 실패:', error);
      setSnackbar({
        open: true,
        message: `백업 삭제 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`,
        severity: 'error'
      });
    }
  };

  // 복원 실행
  const executeRestore = async () => {
    if (!selectedBackup) return;
    
    setRestoring(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/restore/from-backup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          backup_id: selectedBackup.backup_id,
          restore_mode: restoreMode,
          collections: collectionsToRestore,
          confirm_restore: true
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '복원 실패');
      }
      
      const data = await response.json();
      console.log('✅ 데이터 복원 완료:', data);
      
      const totalRestored = Object.values(data.restored_counts || {})
        .reduce((sum: number, count) => sum + (count as number), 0);
      
      setSnackbar({
        open: true,
        message: `데이터 복원이 완료되었습니다! (총 ${totalRestored}개 문서 복원)`,
        severity: 'success'
      });
      
      setRestoreDialog(false);
      setSelectedBackup(null);
      
    } catch (error) {
      console.error('데이터 복원 실패:', error);
      setSnackbar({
        open: true,
        message: `복원 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`,
        severity: 'error'
      });
    } finally {
      setRestoring(false);
    }
  };

  // 상태별 색상 및 아이콘
  const getStatusChip = (status: string) => {
    const configs = {
      completed: { color: 'success' as const, label: '완료', icon: <CheckCircleIcon /> },
      failed: { color: 'error' as const, label: '실패', icon: <ErrorIcon /> },
      in_progress: { color: 'warning' as const, label: '진행중', icon: <CircularProgress size={16} /> }
    };
    
    const config = configs[status as keyof typeof configs] || configs.failed;
    return (
      <Chip 
        size="small" 
        color={config.color} 
        label={config.label} 
        icon={config.icon}
        variant="outlined"
      />
    );
  };

  // 위험도별 색상
  const getRiskColor = (risk: string) => {
    const colors = {
      low: '#4caf50',
      medium: '#ff9800', 
      high: '#f44336'
    };
    return colors[risk as keyof typeof colors] || colors.low;
  };

  useEffect(() => {
    fetchBackups();
    if (activeTab === 3) { // 로그 탭이 활성화된 경우
      fetchLogs();
    }
  }, [fetchBackups, fetchLogs, activeTab]);

  // 자동 새로고침 설정
  useEffect(() => {
    if (!autoRefresh || activeTab !== 3) return;

    const interval = setInterval(() => {
      fetchLogs();
      setLastRefresh(new Date());
    }, 10000); // 10초마다 새로고침

    return () => clearInterval(interval);
  }, [autoRefresh, activeTab, fetchLogs]);

  // 🔥 현재 백업 모드 (application: 매입 데이터, price: 가격 데이터)
  const backupMode = searchParams.get('type') === 'price' ? 'price' : 'application';

  // 🔥 모드 변경 시 탭 초기화 (매입 데이터 모드로 전환 시 첫 번째 탭으로)
  useEffect(() => {
    if (backupMode === 'application') {
      setActiveTab(0);
    }
  }, [backupMode]);

  // 🔥 탭 변경 핸들러 (모드에 따라 다르게 처리)
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* 헤더 섹션 */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          {backupMode === 'price' ? (
            <PriceIcon sx={{ fontSize: { xs: 28, md: 32 }, color: '#1976d2' }} />
          ) : (
            <BackupIcon sx={{ fontSize: { xs: 28, md: 32 }, color: '#1976d2' }} />
          )}
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            fontWeight="bold" 
            sx={{ color: '#1976d2' }}
          >
            {backupMode === 'price' ? '가격 데이터 백업' : '매입 데이터 백업'}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {backupMode === 'price' 
            ? '스마트폰, 태블릿, 웨어러블 가격 데이터의 스냅샷 백업 및 복구'
            : '매입 신청, 알림톡 로그, 관리자 데이터의 안전한 백업 및 복원'
          }
        </Typography>
        
        {/* 실시간 상태 표시 (매입 데이터 모드에서만) */}
        {backupMode === 'application' && (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip 
              size="small" 
              icon={<StorageIcon />} 
              label={`총 ${backups.length}개 백업`} 
              variant="outlined" 
            />
            <Chip 
              size="small" 
              icon={<CheckCircleIcon />} 
              label={`완료 ${backups.filter(b => b.status === 'completed').length}개`} 
              color="success"
              variant="outlined" 
            />
            <Chip 
              size="small" 
              icon={<ScheduleIcon />} 
              label="매일 오전 2시 자동 백업" 
              color="info"
              variant="outlined" 
            />
          </Stack>
        )}
      </Box>

      {/* 탭 네비게이션 */}
      <Card sx={{ mb: 3 }}>
        {/* 🔥 모드에 따라 다른 탭 표시 */}
        {backupMode === 'price' ? (
          // 가격 데이터 모드: 탭 없이 바로 PriceBackupTab 표시
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <PriceBackupTab adminId="admin" />
          </Box>
        ) : (
          // 매입 데이터 모드: 기존 4개 탭 표시
          <>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons={isMobile ? "auto" : false}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  minHeight: { xs: 48, md: 64 },
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }
              }}
            >
              <Tab 
                icon={<BackupIcon />} 
                label="백업 관리" 
                iconPosition={isMobile ? "top" : "start"}
              />
              <Tab 
                icon={<RestoreIcon />} 
                label="복원 관리" 
                iconPosition={isMobile ? "top" : "start"}
              />
              <Tab 
                icon={<AnalyticsIcon />} 
                label="통계 & 분석" 
                iconPosition={isMobile ? "top" : "start"}
              />
              <Tab 
                icon={<ListAltIcon />} 
                label="작업 로그" 
                iconPosition={isMobile ? "top" : "start"}
              />
            </Tabs>

        {/* 백업 관리 탭 */}
        <TabPanel value={activeTab} index={0}>
          {/* 빠른 액션 버튼들 */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  height: '100%', 
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { 
                    boxShadow: 6,
                    transform: 'translateY(-2px)'
                  } 
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={creating ? <CircularProgress size={20} color="inherit" /> : <BackupIcon />}
                    onClick={createManualBackup}
                    disabled={creating}
                    fullWidth
                    size="large"
                    sx={{ mb: 2, py: 1.5 }}
                  >
                    {creating ? '백업 중...' : '수동 백업'}
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    현재 매입 데이터를 즉시 백업
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Button
                    variant="outlined"
                    startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                    onClick={fetchBackups}
                    disabled={loading}
                    fullWidth
                    size="large"
                    sx={{ mb: 2, py: 1.5 }}
                  >
                    {loading ? '조회 중...' : '목록 새로고침'}
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    최신 백업 목록 업데이트
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Button
                    variant="outlined"
                    startIcon={<ScheduleIcon />}
                    fullWidth
                    size="large"
                    sx={{ mb: 2, py: 1.5 }}
                    onClick={async () => {
                      try {
                        const resp = await fetch(`${API_BASE_URL}/api/backup/run-scheduled`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' }
                        });
                        if (!resp.ok) {
                          const err = await resp.json().catch(() => ({}));
                          throw new Error(err.message || `HTTP ${resp.status}`);
                        }
                        const data = await resp.json();
                        setSnackbar({ open: true, message: data.message || '스케줄 백업이 실행되었습니다.', severity: 'success' });
                        fetchBackups();
                      } catch (e: any) {
                        setSnackbar({ open: true, message: `스케줄 백업 실행 실패: ${e.message}`, severity: 'error' });
                      }
                    }}
                  >
                    스케줄 백업 즉시 실행
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    매일 오전 2시 자동 실행 (진단용 수동 트리거)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Button
                    variant="outlined"
                    startIcon={<SecurityIcon />}
                    fullWidth
                    size="large"
                    sx={{ mb: 2, py: 1.5 }}
                    disabled
                  >
                    보안 검증
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    백업 파일 무결성 확인
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* 백업 목록 테이블 */}
          {loading && <LinearProgress sx={{ mb: 2 }} />}
          
          <TableContainer 
            component={Paper} 
            sx={{ 
              maxHeight: 600,
              '& .MuiTableCell-root': {
                fontSize: { xs: '0.75rem', md: '0.875rem' }
              }
            }}
          >
            <Table stickyHeader size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow>
                  <TableCell>백업 정보</TableCell>
                  {!isSmallMobile && <TableCell>타입</TableCell>}
                  <TableCell>상태</TableCell>
                  <TableCell align="right">데이터</TableCell>
                  {!isMobile && <TableCell align="right">크기</TableCell>}
                  <TableCell align="center">작업</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {backups.map((backup) => (
                  <TableRow key={backup.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                          {backup.backup_id.slice(-12)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(backup.created_at).toLocaleString('ko-KR', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    {!isSmallMobile && (
                      <TableCell>
                        <Chip
                          size="small"
                          label={backup.type === 'scheduled' ? '자동' : '수동'}
                          variant="outlined"
                          color={backup.type === 'scheduled' ? 'info' : 'default'}
                        />
                      </TableCell>
                    )}
                    
                    <TableCell>
                      {getStatusChip(backup.status)}
                      {backup.is_expired && (
                        <Chip 
                          size="small" 
                          label="만료" 
                          color="error" 
                          variant="outlined"
                          sx={{ ml: 0.5 }}
                        />
                      )}
                    </TableCell>
                    
                    <TableCell align="right">
                      <Tooltip 
                        title={`신청: ${backup.applications_count}, 알림: ${backup.alerts_count}, 관리자: ${backup.admins_count}`}
                      >
                        <Typography variant="body2" fontWeight="bold">
                          {backup.total_documents.toLocaleString()}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    
                    {!isMobile && (
                      <TableCell align="right">
                        <Typography variant="body2">
                          {backup.file_size_mb} MB
                        </Typography>
                      </TableCell>
                    )}
                    
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="미리보기">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => {
                              setSelectedBackup(backup);
                              showRestorePreview(backup);
                            }}
                            disabled={backup.status !== 'completed' || !backup.file_exists || previewing}
                          >
                            {previewing && selectedBackup?.id === backup.id ? 
                              <CircularProgress size={16} /> : <PreviewIcon />
                            }
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="복원">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setSelectedBackup(backup);
                              setRestoreDialog(true);
                            }}
                            disabled={backup.status !== 'completed' || !backup.file_exists}
                          >
                            <RestoreIcon />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="다운로드">
                          <IconButton
                            size="small"
                            color="secondary"
                            disabled={!backup.file_exists}
                            onClick={() => downloadBackup(backup)}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="삭제">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteBackup(backup)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* 복원 관리 탭 */}
        <TabPanel value={activeTab} index={1}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>복원 작업 안내</AlertTitle>
            복원 작업은 되돌릴 수 없습니다. 반드시 미리보기를 통해 영향도를 확인한 후 진행하세요.
          </Alert>
          
          {/* 복원 옵션 설정 */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                복원 설정
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>복원 모드</InputLabel>
                    <Select
                      value={restoreMode}
                      onChange={(e) => setRestoreMode(e.target.value as 'replace' | 'merge')}
                      label="복원 모드"
                    >
                      <MenuItem value="replace">
                        <Box>
                          <Typography variant="body2"><strong>전체 교체</strong></Typography>
                          <Typography variant="caption" color="text.secondary">
                            기존 데이터를 삭제하고 백업 데이터로 완전 교체
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="merge">
                        <Box>
                          <Typography variant="body2"><strong>병합</strong></Typography>
                          <Typography variant="caption" color="text.secondary">
                            기존 데이터를 유지하고 백업 데이터와 병합
                          </Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    복원할 데이터 선택
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    현재는 <strong>매입 신청 데이터</strong>만 백업 및 복원됩니다.
                  </Alert>
                  <Stack spacing={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={collectionsToRestore.includes('applications')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCollectionsToRestore(['applications']);
                            } else {
                              setCollectionsToRestore([]);
                            }
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">매입 신청 데이터</Typography>
                          <Typography variant="caption" color="text.secondary">
                            고객 신청, 기기 정보, 검수 결과, 매입 상태 등 모든 매입 관련 데이터
                          </Typography>
                        </Box>
                      }
                    />
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </TabPanel>

        {/* 통계 & 분석 탭 */}
        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            {/* 전체 통계 카드 */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <StorageIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                      백업 현황
                    </Typography>
                  </Stack>
                  <Stack spacing={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">총 백업 수</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {backups.length}개
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">완료된 백업</Typography>
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        {backups.filter(b => b.status === 'completed').length}개
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">총 데이터 크기</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {backups.reduce((sum, b) => sum + parseFloat(b.file_size_mb || '0'), 0).toFixed(1)}MB
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* 데이터 분포 */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <DeviceHubIcon color="secondary" />
                    <Typography variant="h6" fontWeight="bold">
                      데이터 분포
                    </Typography>
                  </Stack>
                  <Stack spacing={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">매입 신청</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {backups.reduce((sum, b) => sum + (b.applications_count || 0), 0).toLocaleString()}건
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">알림톡 로그</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {backups.reduce((sum, b) => sum + (b.alerts_count || 0), 0).toLocaleString()}건
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">관리자</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {backups.reduce((sum, b) => sum + (b.admins_count || 0), 0)}명
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* 보안 상태 */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <SecurityIcon color="warning" />
                    <Typography variant="h6" fontWeight="bold">
                      보안 상태
                    </Typography>
                  </Stack>
                  <Stack spacing={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">파일 존재</Typography>
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        {backups.filter(b => b.file_exists).length}개
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">만료된 백업</Typography>
                      <Typography variant="body2" fontWeight="bold" color="error.main">
                        {backups.filter(b => b.is_expired).length}개
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">자동 백업</Typography>
                      <Typography variant="body2" fontWeight="bold" color="info.main">
                        {backups.filter(b => b.type === 'scheduled').length}개
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* 작업 로그 탭 */}
        <TabPanel value={activeTab} index={3}>
          <Grid container spacing={3}>
            {/* 로그 헤더 */}
            <Grid item xs={12}>
              <Stack 
                direction={isMobile ? "column" : "row"} 
                justifyContent="space-between" 
                alignItems={isMobile ? "stretch" : "center"} 
                spacing={2}
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    백업/복원 작업 로그
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    마지막 업데이트: {lastRefresh.toLocaleString('ko-KR')}
                  </Typography>
                </Box>
                
                <Stack direction={isMobile ? "column" : "row"} spacing={1} alignItems="center">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={autoRefresh}
                        onChange={(e) => setAutoRefresh(e.target.checked)}
                        size="small"
                      />
                    }
                    label="자동 새로고침 (10초)"
                    sx={{ mr: 1 }}
                  />
                  
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => exportLogs()}
                      size={isMobile ? "small" : "medium"}
                    >
                      CSV 내보내기
                    </Button>
                    
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setDeleteDialog(true)}
                      disabled={selectedLogIds.length === 0}
                      size={isMobile ? "small" : "medium"}
                    >
                      선택 삭제 ({selectedLogIds.length})
                    </Button>
                    
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<DeleteIcon />}
                      onClick={() => setBulkDeleteDialog(true)}
                      size={isMobile ? "small" : "medium"}
                    >
                      일괄 삭제
                    </Button>
                    
                    <Button
                      variant="outlined"
                      startIcon={logsLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
                      onClick={() => {
                        fetchLogs();
                        setLastRefresh(new Date());
                      }}
                      disabled={logsLoading}
                      size={isMobile ? "small" : "medium"}
                    >
                      {logsLoading ? '로딩 중...' : '새로고침'}
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
              
              {/* 실시간 상태 표시 */}
              {autoRefresh && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">
                      실시간 모니터링 활성화 - 10초마다 자동 업데이트
                    </Typography>
                  </Stack>
                </Alert>
              )}
            </Grid>

            {/* 로그 통계 카드들 */}
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {logs.filter(log => log.status === 'success').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    성공한 작업
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="error.main">
                    {logs.filter(log => log.status === 'failed').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    실패한 작업
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {logs.filter(log => log.status === 'in_progress').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    진행 중인 작업
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {logs.filter(log => log.operation_type === 'backup').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    백업 작업
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* 로그 테이블 */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              indeterminate={selectedLogIds.length > 0 && selectedLogIds.length < logs.length}
                              checked={logs.length > 0 && selectedLogIds.length === logs.length}
                              onChange={toggleAllLogsSelection}
                            />
                          </TableCell>
                          <TableCell>작업 유형</TableCell>
                          <TableCell>상태</TableCell>
                          <TableCell>시작 시간</TableCell>
                          <TableCell>소요 시간</TableCell>
                          <TableCell>처리 문서 수</TableCell>
                          <TableCell>파일 크기</TableCell>
                          <TableCell>트리거</TableCell>
                          <TableCell>상세</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {logs.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                              <Typography color="text.secondary">
                                {logsLoading ? '로그를 불러오는 중...' : '로그가 없습니다.'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          logs.map((log) => (
                            <TableRow key={log.id} selected={selectedLogIds.includes(log.id)}>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedLogIds.includes(log.id)}
                                  onChange={() => toggleLogSelection(log.id)}
                                />
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  {log.operation_type === 'backup' && <BackupIcon fontSize="small" />}
                                  {log.operation_type === 'restore' && <RestoreIcon fontSize="small" />}
                                  {log.operation_type === 'delete' && <DeleteIcon fontSize="small" />}
                                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                    {log.operation_type === 'backup' ? '백업' : 
                                     log.operation_type === 'restore' ? '복원' : '삭제'}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={log.status === 'success' ? '성공' : 
                                        log.status === 'failed' ? '실패' : '진행중'}
                                  color={log.status === 'success' ? 'success' : 
                                        log.status === 'failed' ? 'error' : 'warning'}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {new Date(log.started_at).toLocaleString('ko-KR')}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {log.duration_seconds ? `${log.duration_seconds}초` : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {log.details.documents_count?.toLocaleString() || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {log.details.file_size_mb ? `${log.details.file_size_mb}MB` : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={log.details.trigger === 'manual' ? '수동' : 
                                        log.details.trigger === 'scheduled' ? '자동' : 'API'}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Tooltip title={log.details.error_message || '정상 처리'}>
                                  <IconButton size="small">
                                    <InfoIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
          </>
        )}
      </Card>

      {/* 선택 로그 삭제 확인 다이얼로그 */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <DeleteIcon color="error" />
            <Typography variant="h6">선택한 로그 삭제</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>주의</AlertTitle>
            이 작업은 되돌릴 수 없습니다. 선택한 {selectedLogIds.length}개의 로그가 영구적으로 삭제됩니다.
          </Alert>
          
          <Typography variant="body2" color="text.secondary">
            삭제될 로그 ID: {selectedLogIds.slice(0, 5).join(', ')}
            {selectedLogIds.length > 5 && ` 외 ${selectedLogIds.length - 5}개`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>취소</Button>
          <Button
            onClick={deleteSelectedLogs}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? '삭제 중...' : '삭제 확인'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 일괄 삭제 다이얼로그 */}
      <Dialog
        open={bulkDeleteDialog}
        onClose={() => setBulkDeleteDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <DeleteIcon color="warning" />
            <Typography variant="h6">조건별 일괄 삭제</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>위험</AlertTitle>
            조건에 맞는 모든 로그가 영구적으로 삭제됩니다. 신중하게 선택해주세요.
          </Alert>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="outlined"
                color="warning"
                onClick={() => deleteBulkLogs({ older_than_days: 7 })}
                disabled={deleting}
                sx={{ py: 2 }}
              >
                7일 이상 된 로그 삭제
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="outlined"
                color="warning"
                onClick={() => deleteBulkLogs({ older_than_days: 30 })}
                disabled={deleting}
                sx={{ py: 2 }}
              >
                30일 이상 된 로그 삭제
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="outlined"
                color="warning"
                onClick={() => deleteBulkLogs({ status: 'success', older_than_days: 3 })}
                disabled={deleting}
                sx={{ py: 2 }}
              >
                3일 이상 된 성공 로그만 삭제
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="outlined"
                color="error"
                onClick={() => deleteBulkLogs({ operation_type: 'delete' })}
                disabled={deleting}
                sx={{ py: 2 }}
              >
                삭제 유형 로그만 일괄 삭제
              </Button>
            </Grid>
          </Grid>
          
          {deleting && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                삭제 작업을 진행하고 있습니다...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialog(false)} disabled={deleting}>
            취소
          </Button>
        </DialogActions>
      </Dialog>

      {/* 복원 미리보기 다이얼로그 */}
      <Dialog
        open={previewDialog}
        onClose={() => setPreviewDialog(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PreviewIcon color="info" />
            <Typography variant="h6">복원 미리보기</Typography>
          </Stack>
        </DialogTitle>
        
        <DialogContent>
          {restorePreview && (
            <Box sx={{ pt: 1 }}>
              {/* 전체 위험도 표시 */}
              <Alert 
                severity={
                  restorePreview.overall_impact.risk_level === 'high' ? 'error' :
                  restorePreview.overall_impact.risk_level === 'medium' ? 'warning' : 'success'
                }
                sx={{ mb: 3 }}
              >
                <AlertTitle>
                  전체 위험도: {restorePreview.overall_impact.risk_level.toUpperCase()}
                </AlertTitle>
                {restorePreview.overall_impact.recommendation || '복원을 진행하시기 전에 현재 데이터를 백업하는 것을 권장합니다.'}
              </Alert>

              {/* 백업 정보 */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                    백업 정보
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>백업 ID:</strong> {restorePreview.backup_id}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>생성일시:</strong> {new Date(restorePreview.backup_info.created_at).toLocaleString('ko-KR')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>총 문서 수:</strong> {restorePreview.backup_info.total_documents.toLocaleString()}개
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>도메인:</strong> {restorePreview.backup_info.domain}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* 컬렉션별 영향도 분석 */}
              {Object.entries(restorePreview.comparison_results || {}).map(([collection, result]) => (
                <Accordion key={collection} sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {collection === 'applications' ? '매입 신청' : 
                         collection === 'alerts_log' ? '알림톡 로그' : 
                         collection === 'admins' ? '관리자' : collection}
                      </Typography>
                      <Chip
                        size="small"
                        label={result.impact_analysis.data_loss_risk}
                        sx={{ 
                          bgcolor: getRiskColor(result.impact_analysis.data_loss_risk),
                          color: 'white'
                        }}
                      />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>현재 데이터:</strong> {result.current_count.toLocaleString()}개
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>백업 데이터:</strong> {result.backup_count.toLocaleString()}개
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" sx={{ mb: 1, color: 'success.main' }}>
                          <strong>추가될 데이터:</strong> {result.impact_analysis?.will_be_added || 0}개
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1, color: 'error.main' }}>
                          <strong>손실될 데이터:</strong> {result.impact_analysis?.will_be_lost || 0}개
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'warning.main' }}>
                          <strong>변경될 데이터:</strong> {result.impact_analysis?.will_be_modified || 0}개
                        </Typography>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}

              {/* 권장사항 */}
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                    권장사항
                  </Typography>
                  <List dense>
                    {restorePreview.recommendations.map((rec, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          {rec.type === 'critical' ? <ErrorIcon color="error" /> :
                           rec.type === 'warning' ? <WarningIcon color="warning" /> :
                           rec.type === 'safe' ? <CheckCircleIcon color="success" /> :
                           <InfoIcon color="info" />}
                        </ListItemIcon>
                        <ListItemText 
                          primary={rec.message}
                          secondary={rec.collection ? `컬렉션: ${rec.collection}` : undefined}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setPreviewDialog(false)}>
            닫기
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setPreviewDialog(false);
              setRestoreDialog(true);
            }}
            disabled={false} // 복원 미리보기가 열렸으면 항상 활성화
          >
            복원 진행
          </Button>
        </DialogActions>
      </Dialog>

      {/* 복원 실행 다이얼로그 */}
      <Dialog
        open={restoreDialog}
        onClose={() => setRestoreDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <RestoreIcon color="primary" />
            <Typography variant="h6">데이터 복원</Typography>
          </Stack>
        </DialogTitle>
        
        <DialogContent>
          {selectedBackup && (
            <Box sx={{ pt: 1 }}>
              <Alert severity="error" sx={{ mb: 3 }}>
                <AlertTitle>⚠️ 중요한 주의사항</AlertTitle>
                <Typography variant="body2">
                  이 작업은 <strong>되돌릴 수 없습니다</strong>. 
                  {restoreMode === 'replace' && ' 현재 데이터가 백업 데이터로 완전히 교체됩니다.'}
                  {restoreMode === 'merge' && ' 백업 데이터가 현재 데이터와 병합됩니다.'}
                </Typography>
              </Alert>

              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                    복원 대상 백업
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>백업 ID:</strong> {selectedBackup.backup_id}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>생성일시:</strong> {new Date(selectedBackup.created_at).toLocaleString('ko-KR')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>총 문서 수:</strong> {selectedBackup.total_documents.toLocaleString()}개
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>파일 크기:</strong> {selectedBackup.file_size_mb}MB
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                    복원 설정 확인
                  </Typography>
                  
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>복원 모드:</strong> {restoreMode === 'replace' ? '전체 교체' : '병합'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {restoreMode === 'replace' ? 
                          '기존 데이터를 삭제하고 백업 데이터로 완전 교체합니다.' :
                          '기존 데이터를 유지하고 백업 데이터와 병합합니다.'
                        }
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>복원할 컬렉션:</strong>
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {collectionsToRestore.map(collection => (
                          <Chip
                            key={collection}
                            size="small"
                            label={
                              collection === 'applications' ? '매입 신청' :
                              collection === 'alerts_log' ? '알림톡 로그' :
                              collection === 'admins' ? '관리자' : collection
                            }
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setRestoreDialog(false)} disabled={restoring}>
            취소
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={executeRestore}
            disabled={restoring || collectionsToRestore.length === 0}
            startIcon={restoring ? <CircularProgress size={16} color="inherit" /> : <RestoreIcon />}
          >
            {restoring ? '복원 중...' : '복원 실행'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 알림 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BackupManagementPage;